import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { BulkActionsBar } from "./BulkActionsBar";
import { ScrollableDataGrid } from "./ScrollableDataGrid";

/**
 * SelectableDataGrid
 * Generic wrapper for MUI DataGrid with built-in multi-select and bulk actions bar.
 *
 * Props:
 * - renderBulkActions: ({ selection, clearSelection }) => ReactNode
 * - bulkBarSx: optional sx for BulkActionsBar container
 * - clearSelectionOnRowsChange: default true; clears selection when `rows` changes
 * - All other props are forwarded to ScrollableDataGrid
 */
export function SelectableDataGrid({
  renderBulkActions,
  bulkBarSx,
  clearSelectionOnRowsChange = true,
  rows = [],
  onRowSelectionModelChange,
  ...dataGridProps
}) {
  const createNewSelection = () => ({ ids: new Set(), type: "include" });

  const [selection, setSelection] = useState(createNewSelection());

  useEffect(() => {
    if (clearSelectionOnRowsChange) {
      setSelection(createNewSelection());
    }
  }, [rows, clearSelectionOnRowsChange]);

  const handleSelectionChange = useCallback(
    (model) => {
      setSelection(model);
    },
    [onRowSelectionModelChange]
  );

  const clearSelection = useCallback(() => {
    setSelection(createNewSelection());
  }, []);

  useEffect(() => {
    if (onRowSelectionModelChange) {
      onRowSelectionModelChange(selection);
    }
  }, [selection, onRowSelectionModelChange]);

  return (
    <Box>
      <BulkActionsBar count={selection.ids.size} sx={bulkBarSx}>
        {renderBulkActions
          ? renderBulkActions({ selection, clearSelection })
          : null}
      </BulkActionsBar>
      <ErrorBoundary>
        <ScrollableDataGrid
          rows={Array.isArray(rows) ? rows : []}
          checkboxSelection
          // Keep select-all behavior consistent across all filtered rows by default
          checkboxSelectionVisibleOnly={false}
          // Controlled selection model to keep header checkbox state in sync
          rowSelectionModel={selection}
          // disableRowSelectionOnClick
          hideFooterSelectedRowCount
          onRowSelectionModelChange={handleSelectionChange}
          {...dataGridProps}
        />
      </ErrorBoundary>
    </Box>
  );
}

export default SelectableDataGrid;

// Simple error boundary to prevent DataGrid selection crashes from breaking parent UI
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("SelectableDataGrid caught error", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="error" fontWeight={600}>
            Er is een fout opgetreden bij het laden van de tabel.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Probeer opnieuw
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}
